
import { supabase } from './supabaseClient';
import { LootTable, RewardLog, DigitalReward, LootItem } from '../types';

export const fetchLootTables = async () => {
  const { data, error } = await supabase
    .from('loot_tables')
    .select('*, items:loot_box_items(*)')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as LootTable[];
};

export const createLootTable = async (
  name: string,
  description: string,
  items: LootItem[],
  isMystery: boolean = false,
  pointCost: number = 100,
) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("User not authenticated");

  // 1. Create the parent Loot Table
  const { data: tableData, error: tableError } = await supabase
    .from('loot_tables')
    .insert({
      name,
      description,
      creator_id: user.id,
      is_mystery: isMystery,
      point_cost: pointCost,
    })
    .select()
    .single();

  if (tableError) throw tableError;

  // 2. Create the child Items (Finite Inventory)
  if (items.length > 0) {
      const itemsPayload = items.map(item => ({
          loot_table_id: tableData.id,
          name: item.name,
          type: item.type,
          probability: item.probability,
          value: item.value,
          total_qty: item.total_qty || 100,      // Finite stock
          remaining_qty: item.total_qty || 100,  // Initial remaining
          image_url: item.image_url || null
      }));

      const { error: itemsError } = await supabase
        .from('loot_box_items')
        .insert(itemsPayload);
      
      if (itemsError) throw itemsError;
  }

  return tableData as LootTable;
};

export const updateLootTable = async (
  id: string,
  name: string,
  description: string,
  items: LootItem[],
  isMystery: boolean = false,
  pointCost: number = 100,
) => {
  // 1. Update Parent Table
  const { error: tableError } = await supabase
    .from('loot_tables')
    .update({ name, description, is_mystery: isMystery, point_cost: pointCost })
    .eq('id', id);

  if (tableError) throw tableError;

  // 2. Replace Items (Strategy: Delete all existing for this table, Insert new)
  // This resets the inventory counts to the new 'total_qty', effectively restocking/reconfiguring the box.
  
  // A. Delete existing
  const { error: deleteError } = await supabase
    .from('loot_box_items')
    .delete()
    .eq('loot_table_id', id);

  if (deleteError) throw deleteError;

  // B. Insert new
  if (items.length > 0) {
      const itemsPayload = items.map(item => ({
          loot_table_id: id,
          name: item.name,
          type: item.type,
          probability: item.probability,
          value: item.value,
          total_qty: item.total_qty || 100,
          remaining_qty: item.total_qty || 100, // Reset stock on update
          image_url: item.image_url || null
      }));

      const { error: insertError } = await supabase
        .from('loot_box_items')
        .insert(itemsPayload);

      if (insertError) throw insertError;
  }
};

// --- DIGITAL REWARDS ---

export const fetchDigitalRewards = async () => {
  const { data, error } = await supabase
    .from('digital_rewards')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as DigitalReward[];
};

export const createDigitalReward = async (
    name: string, 
    amount: number, 
    currency: string,
    chain: string = 'BSC',
    contractAddress?: string
) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("User not authenticated");

    const { data, error } = await supabase
      .from('digital_rewards')
      .insert({
        name,
        amount, 
        currency,
        chain,
        contract_address: contractAddress || null,
        creator_id: user.id
      })
      .select()
      .single();

    if (error) throw error;
    return data as DigitalReward;
};

export const deleteDigitalReward = async (id: string) => {
  const { error } = await supabase
    .from('digital_rewards')
    .delete()
    .eq('id', id);

  if (error) throw error;
};

// --- UTILS ---

export const deleteLootTable = async (id: string) => {
  // Cascading delete on DB side should handle items, but for safety:
  const { error } = await supabase
    .from('loot_tables')
    .delete()
    .eq('id', id);

  if (error) throw error;
};

export const fetchRewardLogs = async () => {
  const { data, error } = await supabase
    .from('reward_logs')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(50);

  if (error) throw error;
  return data as RewardLog[];
};

export const fetchUserRewards = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const { data, error } = await supabase
    .from('reward_logs')
    .select('*')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as RewardLog[];
};

// --- COUPON & CLAIM SYSTEM ---

export const issueRewardCoupon = async (
    amount: number | null, 
    currency: string | null, 
    actionType: string = 'DIGITAL_REWARD',
    lootTableId?: string
) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const payload: any = {
        user_id: user.id,
        action_type: actionType,
        status: 'AVAILABLE'
    };

    if (actionType === 'DIGITAL_REWARD') {
        payload.amount = 0; // Placeholder for randomizer
        payload.currency = currency;
    } else if (actionType === 'LOOT_BOX') {
        payload.loot_table_id = lootTableId;
    }

    const { data, error } = await supabase
        .from('reward_logs')
        .insert(payload)
        .select()
        .single();

    if (error) {
        console.error("Error issuing coupon:", error);
        throw error;
    }
    return data as RewardLog;
};

export const claimRewardOnChain = async (couponId: string, walletAddress: string) => {
    // 1. In a real app, this would verify a signature provided by the wallet
    // 2. We verify the user owns the coupon via RLS
    
    // 3. SIMULATE SMART CONTRACT RANDOMIZER
    await new Promise(resolve => setTimeout(resolve, 2500)); // Simulate block time
    
    // Generate random reward between 0.5 and 5.0 USDT (Simulated Contract Logic)
    const revealedAmount = (Math.random() * (5.0 - 0.5) + 0.5).toFixed(2);

    // 4. Update DB to mark as claimed AND set the revealed amount
    const { data, error } = await supabase
        .from('reward_logs')
        .update({
            status: 'CLAIMED',
            amount: parseFloat(revealedAmount),
            wallet_address: walletAddress,
            tx_hash: `0x${Math.random().toString(16).substring(2)}...mock_contract_reveal` 
        })
        .eq('id', couponId)
        .select()
        .single();

    if (error) throw error;
    return data as RewardLog;
};

export const openLootBox = async (logId: string, lootTableId: string) => {
    // Roll + reserve stock with optimistic locking. We retry on contention
    // (another opener grabbed the same unit) by re-rolling against fresh stock.
    // NOTE: this narrows the race window but is not fully atomic; for hard
    // guarantees move the decrement into a Postgres function (SELECT ... FOR UPDATE).
    const MAX_ATTEMPTS = 5;
    let selectedItem: any = null;

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        // 1. Fetch Items currently in stock
        const { data: items } = await supabase
            .from('loot_box_items')
            .select('*')
            .eq('loot_table_id', lootTableId)
            .gt('remaining_qty', 0);

        if (!items || items.length === 0) {
            throw new Error("This box is empty or out of stock.");
        }

        // 2. Weighted Random Selection
        let totalWeight = 0;
        items.forEach((item: any) => totalWeight += item.probability);

        let random = Math.random() * totalWeight;
        let candidate = items[0];

        for (const item of items) {
            if (random < item.probability) {
                candidate = item;
                break;
            }
            random -= item.probability;
        }

        // 3. Reserve Inventory with an optimistic lock: the decrement only
        // succeeds if remaining_qty is still what we read. If 0 rows come back,
        // someone else took it first -> re-roll.
        const { data: reserved } = await supabase.from('loot_box_items')
            .update({ remaining_qty: candidate.remaining_qty - 1 })
            .eq('id', candidate.id)
            .eq('remaining_qty', candidate.remaining_qty)
            .select();

        if (reserved && reserved.length > 0) {
            selectedItem = candidate;
            break;
        }
    }

    if (!selectedItem) {
        throw new Error("This box is busy right now. Please try again.");
    }

    // 4. Update Log
    const { data, error } = await supabase
        .from('reward_logs')
        .update({
            status: 'OPENED',
            item_name: selectedItem.name,
            item_image: selectedItem.image_url,
            action_type: 'LOOT_ITEM_WON' // State change
        })
        .eq('id', logId)
        .select()
        .single();

    if (error) throw error;
    return data as RewardLog;
};

// --- MYSTERY BOX (spend points to open) ---

export const fetchMysteryBoxes = async (): Promise<LootTable[]> => {
  const { data, error } = await supabase
    .from('loot_tables')
    .select('*, items:loot_box_items(*)')
    .eq('is_mystery', true)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data as LootTable[]) || [];
};

export interface MysteryBoxResult {
  ok: boolean;
  reason?: string;
  cost?: number;
  balance?: number;
  item?: { name: string; type: string; image_url?: string; value?: number };
}

// Spends points server-side and returns the won item (or a reason if it failed).
export const openMysteryBox = async (lootTableId: string): Promise<MysteryBoxResult> => {
  const { data, error } = await supabase.rpc('open_mystery_box', { p_loot_table_id: lootTableId });
  if (error) throw error;
  return data as MysteryBoxResult;
};

export const submitShippingInfo = async (logId: string, info: string) => {
    const { data, error } = await supabase
        .from('reward_logs')
        .update({
            status: 'SHIPPED', // Mark as requested
            shipping_info: info
        })
        .eq('id', logId)
        .select()
        .single();
    
    if (error) throw error;
    return data;
};
